import { useCallback, useContext, useEffect, useState } from 'react';
import { ClientContext, useManualQuery } from 'graphql-hooks';
import { createClient } from 'graphql-ws';

import { TOKEN_KEY } from '../context/auth/auth';
import { NUMBER_NOTIFICATIONS_UNREAD, NOTIFICATIONS_UNREAD_COUNT_SUBSCRIPTION } from '../graphql/notification/query';

// The load balancer closes a websocket idle for 60s: a ping every 30s keeps it open.
const KEEP_ALIVE = 30000;
const MAX_RETRY_DELAY = 30000;

// Blocked storage (private window): no token, the server refuses the socket and the client retries later.
const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY) || '';
  } catch (e) {
    return '';
  }
};

const getWsUrl = (url: string) => url.replace(/^http/, 'ws');

// Exponential delay between two reconnections, with a random part so that every tab
// doesn't come back at the same time after a deploy.
const retryWait = (retries: number) => {
  const delay = Math.min(1000 * 2 ** retries, MAX_RETRY_DELAY);
  return new Promise<void>((resolve) => {
    setTimeout(resolve, delay + Math.random() * 1000);
  });
};

// Unread notifications count of the connected user: fetched once, then pushed by the server
// through the notificationsUnreadCount subscription each time it changes.
const useUnreadNotifications = () => {
  const client = useContext(ClientContext);
  const [getNumberUnread] = useManualQuery(NUMBER_NOTIFICATIONS_UNREAD, { skipCache: true });
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    const { data } = await getNumberUnread() as any;
    if (data && typeof data.getNumberNotificationsUnRead === 'number') {
      setCount(data.getNumberNotificationsUnRead);
    }
  }, [getNumberUnread]);

  useEffect(() => {
    if (!client) {
      return undefined;
    }
    let reconnected = false;
    const wsClient = createClient({
      url: getWsUrl(client.url),
      // Read at each connection: the token can change while the tab is open.
      connectionParams: () => ({ Authorization: `Bearer ${getToken()}` }),
      keepAlive: KEEP_ALIVE,
      retryAttempts: Infinity,
      retryWait,
      shouldRetry: () => true,
      on: {
        // Events published while the socket was closed are lost: the count is fetched again.
        connected: () => {
          if (reconnected) {
            refresh();
          }
          reconnected = true;
        },
      },
    });
    const unsubscribe = wsClient.subscribe(
      { query: NOTIFICATIONS_UNREAD_COUNT_SUBSCRIPTION },
      {
        next: ({ data }) => {
          const value = data && (data as { notificationsUnreadCount?: number }).notificationsUnreadCount;
          if (typeof value === 'number') {
            setCount(value);
          }
        },
        error: () => undefined,
        complete: () => undefined,
      },
    );
    refresh();
    return () => {
      unsubscribe();
      wsClient.dispose();
    };
  }, [client, refresh]);

  return { count, setCount, refresh };
};

export default useUnreadNotifications;
